import { BaseItem } from '@algolia/autocomplete-core';
import {
  autocomplete,
  AutocompleteApi,
  AutocompleteOptions,
  AutocompletePlugin,
  AutocompleteSource,
  AutocompleteState,
  getAlgoliaResults,
  HTMLTemplate,
  VNode,
} from '@algolia/autocomplete-js';
import { SearchResponse } from '@algolia/client-search';
import { CommonModule } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  output,
  OutputEmitterRef,
} from '@angular/core';

import { AlgoliaHit } from '@ppg/algolia/models';
import { FeatureModule } from '@ppg/core/content';
import { UrlTarget } from '@ppg/core/enums';
import { Color } from '@ppg/core/models';
import { AppRoutesService, FeatureRoutePathService } from '@ppg/core/navigation';
import { ColorUtils } from '@ppg/shared/color/services';
import { appendImageSize } from '@ppg/shared/product-api';

import { DEBOUNCE_TIME, debouncePromise } from './federated-search-debounce';
import {
  COLOR_SOURCE_ID,
  CONTENT_SOURCE_ID,
  DOCUMENT_SOURCE_ID,
  LINE_CLAMP_TITLE_AND_BODY_DISPLAYED,
  NO_LINE_CLAMP,
  PRODUCT_SOURCE_ID,
  QUERY_MIN_CHARS_NUMBER,
  SourceIdType,
  STATE_SOURCE_TOTAL_HITS_NUMBER_PREFIX,
} from '../data/consts/federated-search.const';
import { ConfigTypes } from '../data/consts/max-characters-configs.const';
import { PluginSettings } from '../data/interfaces/plugin-settings.interface';
import { EllipsisTextPipe } from '../data/pipes/ellipsis-text.pipe';
import { FederatedSearchPluginsService } from '../data/services/federated-search/federated-search-plugins.service';
import { FederatedSearchService } from '../data/services/federated-search/federated-search.service';

@Component({
  selector: 'ppg-federated-search',
  standalone: true,
  imports: [CommonModule],
  providers: [EllipsisTextPipe],
  templateUrl: './federated-search.component.html',
  styleUrl: './federated-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FederatedSearchComponent {
  public readonly searchClickToGlobalSearchPage = output<string>();
  public readonly searchClickToProductPage = output<string>();
  public readonly searchClickToColorsPage = output<string>();
  public readonly searchClickToDocumentResultsPage = output<string>();
  readonly #federatedSearchPluginsService = inject(FederatedSearchPluginsService);
  readonly #colorUtils = inject(ColorUtils);
  readonly #appRoutesService = inject(AppRoutesService);
  readonly #featureRoutePathService = inject(FeatureRoutePathService);
  readonly #federatedSearchService = inject(FederatedSearchService);
  readonly #ellipsisTextPipe = inject(EllipsisTextPipe);

  readonly #fallbackImageSrc = '/assets/img/federated-search-fallback.png';

  readonly #cdeUserCookie = this.#federatedSearchService.cdeUserCookie;
  readonly #textResources = this.#federatedSearchService.textResources;
  readonly #colorIdDisplay = this.#federatedSearchService.colorIdDisplay;
  readonly #maxCharactersConfiguration = this.#federatedSearchService.maxCharactersConfiguration;

  #autocomplete: AutocompleteApi<BaseItem> | null = null;

  readonly #plugins = computed(() => {
    const pluginsSettings = this.#federatedSearchPluginsService.pluginsSettings();
    const plugins = [] as AutocompletePlugin<AlgoliaHit, unknown>[];

    pluginsSettings.forEach((setting) => {
      plugins.push(setting ? this.createPlugin(setting) : ([] as AutocompletePlugin<AlgoliaHit, unknown>));
    });

    return plugins;
  });

  public readonly autocompleteOptions = computed<AutocompleteOptions<BaseItem>>(() => {
    const { searchPlaceholder, noResultsMessage } = this.#textResources();

    const maxWidthStyle = `
      max-width: 540px;
    `;

    const renderStyle = `
      gap: var(--spacing-4XL);
      padding: var(--spacing-2XL) var(--spacing-6XL);
    `;

    const noResultMessageStyle = `
      color: var(--fg-primary);
      font-family: var(--typography-font_family-body);
      font-size: var(--typography-font_size-14);
      font-style: normal;
      font-weight: 400;
      line-height: var(--typography-line_height-20);
    `;

    const columnDataTestId = (columnKey: string): string | undefined => {
      switch (columnKey as SourceIdType) {
        case PRODUCT_SOURCE_ID:
          return 'federatedSearch.products';
        case COLOR_SOURCE_ID:
          return 'federatedSearch.colors';
        case CONTENT_SOURCE_ID:
          return 'federatedSearch.content';
        case DOCUMENT_SOURCE_ID:
          return 'federatedSearch.documents';
        default:
          return undefined;
      }
    };

    return {
      container: '#federated-search-autocomplete',
      openOnFocus: true,
      panelPlacement: 'full-width',
      placeholder: searchPlaceholder,
      detachedMediaQuery: 'none',
      classNames: {
        form: 'flex-row-reverse',
        input: 'pl-3',
        clearButton: 'hidden',
        panel: 'border-noround mt-3',
      },
      insights: {
        insightsInitParams: {
          userToken: this.#cdeUserCookie,
        },
      },
      onStateChange: ({ prevState, state }) => {
        if (state.query !== prevState.query) {
          this.#federatedSearchService.pushLiveSearchResults(state.query);
        }
      },
      onSubmit: ({ state, refresh }) => {
        refresh();
        this.#federatedSearchService.pushSubmitSearchResults(state.query);
      },
      plugins: this.#plugins(),
      renderNoResults({ html, state, render }, root) {
        if (state.query.length >= QUERY_MIN_CHARS_NUMBER) {
          render(
            html`<div class="flex" style="${renderStyle}">
              <div style="${noResultMessageStyle}" class="w-full" data-test-id="federatedSearch.noResultsForQueryText">
                ${noResultsMessage} '${state.query}'
              </div>
            </div>` as VNode,
            root,
          );
        }
      },
      render({ render, html, elements, state }, root) {
        if (state.query.length >= QUERY_MIN_CHARS_NUMBER) {
          render(
            html`<div>
              <div class="flex flex-column md:flex-row" style="${renderStyle}">
                ${Object.keys(elements).map(
                  (key) =>
                    html`<div
                      class="${elements[key] ? 'w-full' : 'hidden'}"
                      style="${maxWidthStyle}"
                      data-test-id="${columnDataTestId(key)}">
                      ${elements[key]}
                    </div> `,
                )}
              </div>
            </div>` as VNode,
            root,
          );
        } else {
          render(html`` as VNode, root);
        }
      },
    };
  });

  constructor() {
    afterNextRender(() => {
      this.#autocomplete = autocomplete(this.autocompleteOptions());
    });

    effect(() => {
      if (this.#autocomplete) {
        this.#autocomplete.destroy();
        this.#autocomplete = autocomplete(this.autocompleteOptions());
      }
    });
  }

  createPlugin({
    searchClient,
    sourceId,
    indexName,
    hitsPerPage,
    facetFilters,
    header,
  }: PluginSettings): AutocompletePlugin<AlgoliaHit, unknown> {
    const textResources = this.#textResources();
    const searchClickToGlobalSearchPage = this.searchClickToGlobalSearchPage;
    const searchClickToProductPage = this.searchClickToProductPage;
    const searchClickToColorsPage = this.searchClickToColorsPage;
    const searchClickToDocumentResultsPage = this.searchClickToDocumentResultsPage;

    const lineClampStyle = (lines: number) => `
      display: -webkit-box;
      -webkit-line-clamp: ${lines};
      -webkit-box-orient: vertical;
      overflow: hidden;
      overflow-wrap: anywhere;
    `;

    const headerStyle = `
      font-weight: 700;
    `;

    const titleStyle = `
      color: var(--fg-primary,  #081520);
      font-family: var(--typography-font_family-body);
      font-size: var(--typography-font_size-16);
      font-style: normal;
      font-weight: 500;
      line-height: var(--typography-line_height-24);
    `;

    const bodyStyle = `
      color: var(--fg-secondary, #3B4A54);
      font-family: var(--typography-font_family-body);
      font-size: var(--typography-font_size-14);
      font-style: normal;
      font-weight: 400;
      line-height: var(--typography-line_height-20);
    `;

    const maxCharactersConfig = (configType: ConfigTypes, numberOfColumns: number) =>
      this.#maxCharactersConfiguration()
        ?.find((x) => x.configType == configType)
        ?.configs?.find((x) => x.numberOfColumns == numberOfColumns);

    const title = (item: AlgoliaHit) => {
      switch (sourceId) {
        case PRODUCT_SOURCE_ID:
          return item['Name'];

        case COLOR_SOURCE_ID:
          return item['ColorName'];

        case DOCUMENT_SOURCE_ID:
          return item['DocumentName'];

        default:
          return item.title;
      }
    };

    const itemTitleTemplate = (html: HTMLTemplate, item: AlgoliaHit, state: AutocompleteState<AlgoliaHit>) => {
      const configTypeValue = configType(item, 'titleOnly');
      const config = maxCharactersConfig(configTypeValue, numberOfColumnsDisplayed(state));
      const lineClamp = configTypeValue == 'titleAndBody' ? LINE_CLAMP_TITLE_AND_BODY_DISPLAYED : NO_LINE_CLAMP;
      const titleValue = title(item);

      return titleValue
        ? html`<div style="${titleStyle} ${lineClampStyle(lineClamp)}" data-test-id="federatedSearch.itemTitle">
            ${this.#ellipsisTextPipe.transform(title(item), config?.maxCharactersTitle)}
          </div>`
        : null;
    };

    const body = (item: AlgoliaHit) => {
      switch (sourceId) {
        case CONTENT_SOURCE_ID:
          return item.description;

        case COLOR_SOURCE_ID:
          return this.#colorIdDisplay() ? item['ColorCode'] : '';

        case DOCUMENT_SOURCE_ID:
          return item['DocumentDescription'];

        default:
          return '';
      }
    };

    const itemBodyTemplate = (html: HTMLTemplate, item: AlgoliaHit, state: AutocompleteState<AlgoliaHit>) => {
      const config = maxCharactersConfig(configType(item, 'bodyOnly'), numberOfColumnsDisplayed(state));
      const bodyValue = body(item);

      return bodyValue
        ? html`<div style="${bodyStyle} ${lineClampStyle(NO_LINE_CLAMP)}" data-test-id="federatedSearch.itemBody">
            ${this.#ellipsisTextPipe.transform(body(item), config?.maxCharactersBody)}
          </div>`
        : null;
    };

    const configType = (item: AlgoliaHit, originalConfigType: ConfigTypes): ConfigTypes => {
      if (title(item) && body(item)) {
        return 'titleAndBody';
      }

      return originalConfigType;
    };

    const itemImage = (html: HTMLTemplate, item: AlgoliaHit) => {
      switch (sourceId) {
        case PRODUCT_SOURCE_ID:
          return imageWithStandardFallbackBehaviour(html, item['ImageURL']);

        case COLOR_SOURCE_ID:
          return imageWithColorFallbackBehaviour(html, item['ColorImage'], item['RGB']);

        case CONTENT_SOURCE_ID:
          return imageWithStandardFallbackBehaviour(html, item.image_url);

        default:
          return null;
      }
    };

    const imageWithStandardFallbackBehaviour = (html: HTMLTemplate, itemImageSrc: string | undefined) => {
      if (itemImageSrc) {
        return image(html, itemImageSrc);
      }
      return image(html, this.#fallbackImageSrc);
    };

    const imageWithColorFallbackBehaviour = (
      html: HTMLTemplate,
      itemImageSrc: string | undefined,
      rgb: string | undefined,
    ) => {
      if (itemImageSrc) {
        return image(html, itemImageSrc);
      } else if (rgb) {
        return colorBackground(html, rgb);
      }
      return image(html, this.#fallbackImageSrc);
    };

    const image = (html: HTMLTemplate, src: string) => {
      const optimizedImg = appendImageSize(src, 'SmallOptimized54');
      return html`<img
        class="h-4rem w-4rem min-w-4rem"
        style="object-fit: cover"
        src="${optimizedImg}"
        onError="${handleOnImageError}" />`;
    };

    const handleOnImageError = (event: Event) => {
      const img = event.target as HTMLImageElement;
      img.src = this.#fallbackImageSrc;
    };

    const colorBackground = (html: HTMLTemplate, rgb: string) => {
      const transformedRgb = this.#colorUtils.stringToRgb(rgb);
      const backgroundColor = `${this.#colorUtils.rgbToHex(transformedRgb)}`;

      return html`<dev class="h-4rem w-4rem min-w-4rem" style="background-color: ${backgroundColor}"></div>`;
    };

    const searchAllHover = (e: MouseEvent) => {
      (e.target as HTMLElement).style.color = 'var(--fg-brand-tertiary-hover)';
    };

    const searchAllEnabled = (e: MouseEvent) => {
      (e.target as HTMLElement).style.color = 'var(--fg-brand-tertiary)';
    };

    const searchButton = (html: HTMLTemplate, setIsOpen: (value: boolean) => void, query: string) => {
      return html` <a
        class="p-button p-button-text gap-2 md:gap-3"
        onclick="${() =>
          this.handleSearchButtonClick(
            sourceId,
            query,
            header,
            setIsOpen,
            searchClickToProductPage,
            searchClickToGlobalSearchPage,
            searchClickToColorsPage,
            searchClickToDocumentResultsPage,
            this.#federatedSearchService,
          )}"
        onmouseover="${searchAllHover}"
        onmouseout="${searchAllEnabled}"
        data-test-id="federatedSearch.searchButton">
        <span class="p-button-label" data-test-id="federatedSearch.searchButtonLabel">${textResources.searchAll}</span>
        <i class="pi pi-chevron-right text-xs" />
      </a>`;
    };

    const debounced = debouncePromise<AutocompleteSource<AlgoliaHit>>((items) => Promise.resolve(items), DEBOUNCE_TIME);
    const getItemDetailsPageUrl = (sourceId: string, item: AlgoliaHit): string | undefined => {
      switch (sourceId) {
        case PRODUCT_SOURCE_ID:
          return `${this.#appRoutesService.getPathByAppRouteFeatureModule(FeatureModule.ProductJourneyDetails)}/${this.#customEncodeURIComponent(item['Name'])}/${item.objectID}`;

        case COLOR_SOURCE_ID:
          return this.#featureRoutePathService.getColorDetailsPath({
            colorName: item['ColorName'],
            productNumber: item.objectID,
          } as Color);

        case DOCUMENT_SOURCE_ID:
          return item['Url'];

        default:
          return item.page_url;
      }
    };

    const getItemDetailsPageTarget = (sourceId: string): string => {
      return sourceId === DOCUMENT_SOURCE_ID ? UrlTarget.BLANK : UrlTarget.SELF;
    };

    const numberOfColumnsDisplayed = (state: AutocompleteState<AlgoliaHit>): number => {
      const contentDisplayed = getTotalForSource(state, CONTENT_SOURCE_ID) ? 1 : 0;
      const colorDisplayed = getTotalForSource(state, COLOR_SOURCE_ID) ? 1 : 0;
      const productsDisplayed = getTotalForSource(state, PRODUCT_SOURCE_ID) ? 1 : 0;
      return contentDisplayed + colorDisplayed + productsDisplayed;
    };

    const getTotalForSource = (state: AutocompleteState<AlgoliaHit>, sourceId: string): number =>
      state.context[STATE_SOURCE_TOTAL_HITS_NUMBER_PREFIX + sourceId] as number;

    return {
      getSources({ query, setIsOpen }) {
        return debounced([
          {
            sourceId,
            getItems({ setContext }) {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName,
                    query,
                    params: { hitsPerPage, facetFilters },
                  },
                ],
                transformResponse({ hits, results }) {
                  const result = results[0] as SearchResponse<AlgoliaHit>;
                  setContext({ [STATE_SOURCE_TOTAL_HITS_NUMBER_PREFIX + sourceId]: result.nbHits });
                  return hits;
                },
              });
            },
            templates: {
              header({ html, state }) {
                return html`
                  <div class="flex align-items-center justify-content-between" data-test-id="federatedSearch.header">
                    <h6 style="${headerStyle}" class="p-2 m-1" data-test-id="federatedSearch.headerLabel">
                      ${header} (${getTotalForSource(state, sourceId)} ${textResources.found})
                    </h6>
                    <div class="block md:hidden">${searchButton(html, setIsOpen, query)}</div>
                  </div>
                `;
              },
              item({ item, html, state }) {
                return html`
                  <a
                    class="no-underline"
                    style="color:inherit; min-height:80px;"
                    href="${getItemDetailsPageUrl(sourceId, item)}"
                    data-test-id="federatedSearch.item"
                    target="${getItemDetailsPageTarget(sourceId)}">
                    <div class="flex gap-3 m-2">
                      ${itemImage(html, item)}
                      <div class="flex flex-column justify-content-center gap-1">
                        ${itemTitleTemplate(html, item, state)} ${itemBodyTemplate(html, item, state)}
                      </div>
                    </div>
                  </a>
                `;
              },
              footer({ html }) {
                return html` <div class="py-3 hidden md:block" data-test-id="federatedSearch.footer">
                  ${searchButton(html, setIsOpen, query)}
                </div>`;
              },
            },
          },
        ]);
      },
    };
  }

  public handleSearchButtonClick(
    sourceId: SourceIdType,
    query: string,
    header: string,
    setIsOpen: (value: boolean) => void,
    searchClickToProductPage: OutputEmitterRef<string>,
    searchClickToGlobalSearchPage: OutputEmitterRef<string>,
    searchClickToColorSearchPage: OutputEmitterRef<string>,
    searchClickToDocumentResultsPage: OutputEmitterRef<string>,
    federatedSearchService: FederatedSearchService,
  ) {
    switch (sourceId) {
      case PRODUCT_SOURCE_ID:
        searchClickToProductPage.emit(query);
        break;
      case CONTENT_SOURCE_ID:
        searchClickToGlobalSearchPage.emit(query);
        break;
      case COLOR_SOURCE_ID:
        searchClickToColorSearchPage.emit(query);
        break;
      case DOCUMENT_SOURCE_ID:
        searchClickToDocumentResultsPage.emit(query);
        break;
      default:
        break;
    }
    setIsOpen(false);
    federatedSearchService.pushSearchAllResults(query, header);
  }

  /**
   * Encodes a URI component by escaping special characters, with additional handling
   * to ensure that parentheses `(` and `)` are percent-encoded as `%28` and `%29`.
   *
   * @param value - The string to encode as a URI component.
   * @returns The encoded URI component string with parentheses properly escaped.
   */
  #customEncodeURIComponent(value: string): string {
    return encodeURIComponent(value).replace(/\(/g, '%28').replace(/\)/g, '%29');
  }
}
